// src/components/ml/mlFormat.js
// Shared formatting + labels for the AI Intelligence Center (pure functions, no React).
import { getAuthToken, getUserInfo } from '../../utils/auth';
import { hasRole } from '../../config/roles';

export const fmtMetric = (v, metric) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  return metric?.format === 'ratio' ? `${(n * 100).toFixed(1)}%` : n.toFixed(2);
};

export const fmtNumber = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

export const fmtBytes = (b) => {
  const n = Number(b) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
};

export const timeAgo = (d) => {
  if (!d) return '—';
  const ms = Date.now() - new Date(d).getTime();
  if (!Number.isFinite(ms)) return '—';
  const s = Math.round(ms / 1000);
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
};

export const fmtDate = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : x.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const STATUS_COLOR = { production: 'green', staging: 'purple', archived: 'gray' };
export const JOB_COLOR = { queued: 'gray', running: 'blue', succeeded: 'green', skipped: 'yellow', failed: 'red', cancelled: 'gray' };
export const ENGINE_COLOR = { online: 'green', degraded: 'orange', idle: 'blue', offline: 'red', not_installed: 'gray', unknown: 'gray' };
export const DECISION_LABEL = {
  promoted: 'Promoted to production',
  kept_champion: 'Production kept',
  awaiting_approval: 'Waiting for approval',
  rejected_safeguard: 'Rejected (safeguard)',
  skipped: 'Skipped',
};
export const DECISION_COLOR = { promoted: 'green', kept_champion: 'gray', awaiting_approval: 'purple', rejected_safeguard: 'red', skipped: 'yellow' };
export const TRIGGER_LABEL = { manual: 'Manual', autopilot: 'Autopilot', schedule: 'Schedule', drift: 'Drift', api: 'API' };
export const EVENT_LABEL = {
  trained: 'Trained', promoted: 'Promoted', rolled_back: 'Rolled back', archived: 'Archived', deleted: 'Deleted',
  kept_champion: 'Kept production', rejected: 'Rejected', training_failed: 'Training failed', training_skipped: 'Training skipped',
  training_cancelled: 'Run cancelled', policy_changed: 'Policy changed', cache_reset: 'Cache reset',
};
export const EVENT_COLOR = {
  trained: 'blue', promoted: 'green', rolled_back: 'orange', archived: 'gray', deleted: 'red', kept_champion: 'gray',
  rejected: 'red', training_failed: 'red', training_skipped: 'yellow', training_cancelled: 'gray', policy_changed: 'purple', cache_reset: 'cyan',
};
export const LEVEL_COLOR = { high: 'red', medium: 'orange', low: 'blue' };

/** Signed-in user (sessionStorage user info, else JWT payload). UI hints only — the API enforces access. */
export function currentUser() {
  try {
    const info = getUserInfo?.();
    if (info && (info.role || info.id)) return info;
  } catch (_) { /* fall through */ }
  try {
    const token = getAuthToken?.();
    const part = token && String(token).split('.')[1];
    if (part) {
      const json = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
      return json.user || json;
    }
  } catch (_) { /* not a JWT */ }
  return null;
}
export const canManageModels = (user) => hasRole(user, '@supervision');
export const canDeleteModels = (user) => hasRole(user, 'admin');
export const canViewOps = (user) => hasRole(user, '@office');

/** Normalises a websocket message to the ml_events payload (layers differ in how they wrap). */
export function mlEventOf(msg) {
  for (const c of [msg?.payload, msg?.data, msg]) {
    if (c && typeof c === 'object' && (c.event || c.jobId || c.modelType)) return { ...c, event: c.event || c.type };
  }
  return null;
}
